/**
 * Script blocking via wrapper approach.
 *
 * Blocked scripts use:
 *   <script type="text/plain" data-cmp-category="analytics" src="..."></script>
 *
 * On consent, these are transformed to real <script> elements and executed.
 *
 * IMPORTANT — Sequential loading:
 * Scripts are activated in DOM order within each category. External scripts
 * (src) are loaded with onload/onerror chaining so that dependent inline
 * scripts only run after the library they depend on has finished loading.
 *
 * Example:
 *   <script type="text/plain" data-cmp-category="analytics"
 *           src="https://www.googletagmanager.com/gtag/js?id=G-XXX"></script>
 *   <script type="text/plain" data-cmp-category="analytics">
 *     gtag('config', 'G-XXX');   // ← safe: runs AFTER the src script loads
 *   </script>
 */

const CMP_ATTR = "data-cmp-category";
const BLOCKED_TYPE = "text/plain";
const ACTIVATED_ATTR = "data-cmp-activated";

/** Set of script identifiers already activated in this page session */
const activatedSet = new Set<string>();

/** Promise that resolves when the current activation chain finishes */
let activationChain: Promise<void> = Promise.resolve();

// ── Helpers ──────────────────────────────────────────────────────

/**
 * Find all blocked scripts in the DOM (not yet activated).
 * Returns them in DOM order (querySelectorAll guarantees this).
 */
export function findBlockedScripts(): HTMLScriptElement[] {
  return Array.from(
    document.querySelectorAll<HTMLScriptElement>(
      `script[type="${BLOCKED_TYPE}"][${CMP_ATTR}]:not([${ACTIVATED_ATTR}])`
    )
  );
}

/** Build a unique key for dedup tracking. */
function scriptKey(script: HTMLScriptElement): string {
  const src = script.getAttribute("src") || "";
  const cat = script.getAttribute(CMP_ATTR) || "";
  const inline = src ? "" : (script.textContent?.slice(0, 100) || "");
  return `${cat}::${src}::${inline}`;
}

// ── Single script injection ──────────────────────────────────────

/**
 * Inject a single script and return a Promise that resolves when it's done.
 *
 * - External (src): resolves on `onload`, rejects on `onerror`
 * - Inline: resolves immediately after insertion (sync execution)
 */
function injectScript(original: HTMLScriptElement): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const newScript = document.createElement("script");

    // Copy all attributes except type
    for (const attr of Array.from(original.attributes)) {
      if (attr.name === "type") continue;
      newScript.setAttribute(attr.name, attr.value);
    }

    newScript.type = "text/javascript";
    newScript.setAttribute(ACTIVATED_ATTR, "true");

    const hasSrc = !!original.getAttribute("src");

    if (hasSrc) {
      // External script: wait for download + execution
      newScript.onload = () => {
        console.info(
          `[CMP] External script loaded: src="${original.getAttribute("src")}"`
        );
        resolve();
      };
      newScript.onerror = () => {
        console.warn(
          `[CMP] External script failed: src="${original.getAttribute("src")}"`
        );
        // Resolve anyway so the chain continues (don't block siblings)
        resolve();
      };
    } else if (original.textContent) {
      // Inline script: copy content
      newScript.textContent = original.textContent;
    }

    // Replace the blocked element with the active one (maintains DOM position)
    original.parentNode?.replaceChild(newScript, original);

    // Inline scripts execute synchronously on insertion
    if (!hasSrc) {
      resolve();
    }
  });
}

// ── Activation (sequential, ordered) ─────────────────────────────

/**
 * Activate scripts for given categories, in DOM order, sequentially.
 *
 * External scripts wait for `onload` before the next script in the
 * chain runs, so `gtag('config', ...)` won't fire before `gtag/js`
 * has finished loading.
 *
 * Safe to call multiple times — already-activated scripts are skipped.
 *
 * @returns Promise that resolves with the count of newly activated scripts.
 */
export async function activateScripts(
  allowedCategories: string[]
): Promise<number> {
  const blocked = findBlockedScripts();
  const toActivate: HTMLScriptElement[] = [];

  for (const script of blocked) {
    const category = script.getAttribute(CMP_ATTR);
    if (!category || !allowedCategories.includes(category)) continue;

    const key = scriptKey(script);

    if (activatedSet.has(key)) {
      script.setAttribute(ACTIVATED_ATTR, "true");
      continue;
    }

    activatedSet.add(key);
    toActivate.push(script);
  }

  if (toActivate.length === 0) return 0;

  // Chain onto the existing activation promise so parallel calls
  // don't interleave (e.g. two categories activating at once)
  const work = async () => {
    for (const script of toActivate) {
      const category = script.getAttribute(CMP_ATTR) || "?";
      const src = script.getAttribute("src");

      console.info(
        `[CMP] Activating: category="${category}"${src ? ` src="${src}"` : " (inline)"}`
      );

      await injectScript(script);
    }

    console.info(
      `[CMP] ${toActivate.length} script(s) activated sequentially ` +
        `for [${allowedCategories.join(", ")}]`
    );
  };

  activationChain = activationChain.then(work);
  await activationChain;

  return toActivate.length;
}

// ── Re-trigger ───────────────────────────────────────────────────

/**
 * Re-evaluate and activate scripts based on updated consent.
 * Called when user changes preferences (Save button).
 *
 * Only activates NEWLY allowed scripts. Already-activated ones are skipped.
 * Scripts run in DOM order; external scripts wait for onload before the
 * next script fires.
 */
export async function retriggerScripts(
  consent: Record<string, boolean>
): Promise<number> {
  const allowed = Object.entries(consent)
    .filter(([, v]) => v)
    .map(([k]) => k);

  return activateScripts(allowed);
}

// ── Deactivation / Info ──────────────────────────────────────────

/**
 * Already-executed scripts cannot be truly undone in the browser.
 * Clearing consent + page reload is the correct approach.
 */
export function deactivateScripts(_categories: string[]): void {
  console.info(
    "[CMP] Script deactivation requires page reload to take full effect."
  );
}

/**
 * Get stats about current blocking state.
 */
export function getBlockingStats(): { blocked: number; activated: number } {
  return {
    blocked: findBlockedScripts().length,
    activated: activatedSet.size,
  };
}
